import React, { useContext } from 'react';
import styled from 'styled-components';
import { registerZeptoAccount } from '../../../api/deposit';
import PortalSessionContext from '../../../contexts/PortalSessionContext/PortalSessionContext';

const PayIDDetail = () => {
	const { user } = useContext(PortalSessionContext);
	const [payid, setPayid] = React.useState(user?.payid_details?.payid);
	const [isLoading, setIsLoading] = React.useState(false);
	const [errorMessage, setErrorMessage] = React.useState(user?.id_verified ? '' : 'Please verify your account first');
	const updatePayid = React.useCallback(async (value) => {
		setPayid(value);
	}, []);

	const updateErrorMessage = React.useCallback(async (value) => {
		setErrorMessage(value);
	}, []);
	return (
		<Container style={{ backgroundColor: 'white' }}>
			<HeaderContainer>
				<div style={{ flex: 7, display: 'flex', flexDirection: 'column' }}>
					<Content style={{ color: 'white' }}>Deposit PayID</Content>
					<Title active={!!payid && !isLoading}>{payid && !isLoading ? payid : 'Generate your PayID'}</Title>
				</div>
				<div style={{ flex: 3 }}>
					{user?.id_verified && (
						<PayIDButton
							updateErrorMessage={updateErrorMessage}
							setIsLoading={setIsLoading}
							isLoading={isLoading}
							updatePayid={updatePayid}
							payid={payid}
						/>
					)}
				</div>
			</HeaderContainer>
			<HeaderTitle
				style={{
					color: 'red',
				}}
			>
				{errorMessage}
			</HeaderTitle>
			<StepContainer>
				<HeaderTitle>How to use PayID with us:</HeaderTitle>
				{StepString.map((item, index) => {
					return (
						<PointContainer key={item}>
							<Content style={{ marginRight: 5 }}>{index + 1}.</Content>
							<Content>{item}</Content>
						</PointContainer>
					);
				})}
				<NoteContainer>
					<Content>
						<BlodText>Please note</BlodText> : your very first PayID deposit to PlayUp may not be instant. Some banks
						may not allow or may delay PayID transfers over a certain amount. If your balance hasn’t been updated,
						contact your bank. Bank Account must be in the registered account’s name!
					</Content>
				</NoteContainer>
			</StepContainer>
		</Container>
	);
};

const PayIDButton = ({
	payid,
	updatePayid,
	setIsLoading,
	isLoading,
	updateErrorMessage,
}: {
	isLoading: boolean;
	payid?: string;
	updatePayid: (value: string) => void;
	setIsLoading: (value) => void;
	updateErrorMessage: (value) => void;
}) => {
	const [status, setStatus] = React.useState<'generate' | 'copy' | 'copied'>(payid ? 'copy' : 'generate');

	React.useEffect(() => {
		if (!payid) setStatus('generate');
	}, [payid]);

	const onPress = React.useCallback(async () => {
		if (status === 'generate') {
			try {
				setIsLoading(true);
				const response = await registerZeptoAccount();
				updatePayid(response.data.payid);
				setStatus('copy');
			} catch (error) {
				//@ts-ignore
				updateErrorMessage(error?.errors?.error?.[0] ?? 'Something went wrong, please try again later');
				console.log(error);
			} finally {
				setTimeout(() => {
					setIsLoading(false);
				}, 5000);
			}
		} else if (status === 'copy') {
			navigator.clipboard.writeText(payid!);
			setStatus('copied');
			setTimeout(() => {
				setStatus('copy');
			}, 3000);
		}
	}, [payid, setIsLoading, status, updatePayid]);

	return (
		<Button disabled={isLoading} onClick={onPress}>
			{isLoading ? <Spinner /> : status.toUpperCase()}
		</Button>
	);
};

const Spinner = styled.div`
	width: 20;
	height: 20;
	borderRadius: 10;
	borderWidth: 2;
	borderColor: #fff;
	borderTopColor: #00F181;
	borderStyle: solid;
	animation: spin 1s linear infinite;
	@keyframes spin: {
		0%: {
			transform: rotate(0deg);
		},
		100%: {
			transform: rotate(360deg);
		},
	},
`;

const Button = styled.button({
	backgroundColor: '#00F181',
	paddingTop: 12,
	paddingBottom: 12,
	paddingLeft: 18.5,
	paddingRight: 18.5,
	borderRadius: 5,
	minWidth: 100,
	display: 'flex',
	justifyContent: 'center',
	alignItems: 'center',
	height: 40,
	color: '#000',
	fontWeight: '600',
});

const StepString = [
	'Copy your PlayUp PayID',
	'Log in to your mobile or Internet banking',
	'Select PayID as a payment option',
	'Select the PayID email option instead of using a BSB and account number',
	'Paste your PayID and transfer the funds',
];

const Container = styled.div({
	backgroundColor: '#fff',
});

const HeaderContainer = styled.div({
	backgroundColor: '#252525',
	paddingTop: 10,
	paddingBottom: 10,
	width: '100%',
	flex: 1,
	flexDirection: 'row',
	paddingLeft: 10,
	paddingRight: 10,
	display: 'flex',
});

const Title = styled.span<{ active: boolean }>(({ active }) => ({
	color: active ? '#00F181' : '#eeeeee',
	marginTop: 5,
	fontSize: 13,
	fontWeight: 'bold',
}));

const HeaderTitle = styled.span({
	color: 'black',
	fontSize: 13,
	fontWeight: 'bold',
	marginBottom: 7,
});

const PointContainer = styled.div({
	flexDirection: 'row',
	marginBottom: 5,
});

const Content = styled.span({
	fontSize: 11,
	color: '#000000',
});

const BlodText = styled.span({
	fontWeight: 'bold',
	color: '#000000',
});

const StepContainer = styled.div({
	marginHorizontal: 22,
	marginTop: 13,
});

const NoteContainer = styled.div({
	backgroundColor: '#F2F2F2',
	padding: 10,
	borderRadius: 5,
	marginTop: 8,
});

export default PayIDDetail;
